import { useEffect, useState } from "react";
import { Wallet as WalletType, Transaction } from "./types"; // add Transaction
import { fetchWallets } from "./API/services";
import EmptyWalletState from "./EmptyWalletState";
import WalletCard from "./WalletCard";
import { useModal } from "../../hooks/useModal";
import CreateWalletModal from "./modals/CreateWalletModal";
import PageBreadcrumb from "../common/PageBreadCrumb";
import PageMeta from "../common/PageMeta";
import Spinner from "../ui/Spinner/Spinner";
import WalletActionsModal from "./modals/WalletActionModal";
import TransactionsModal from "./modals/TransactionModal";
import WithdrawModal from "./modals/WithdrawalModal";
import FundWalletModal from "./modals/FundModal";
import TransferModal from "./modals/TransferModal";
import TransactionSummary from "./modals/TransactionSummary"; // add — adjust path if different
import Toast from "../ui/toast/Toast";
import { cache } from "../../utils";
import { User } from "../../stores/useUserStore";
import AddPhoneNumber from "../auth/AddPhoneNumber";
import { RefreshIcon } from "../../icons";
import { useNavigate } from "react-router";

const Wallet = () => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState<WalletType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWallet, setSelectedWallet] = useState<WalletType | null>(null);
  const [showAddPhoneNumber, setShowAddPhoneNumber] = useState(false);
  const [phoneRequired, setPhoneRequired] = useState(false);
  const [summaryTransaction, setSummaryTransaction] = useState<Transaction | null>(null); // add

  // ----- Modals
  const create = useModal();
  const actions = useModal();
  const fund = useModal();
  const withdraw = useModal();
  const transactions = useModal();
  const transfer = useModal();

  const cachedUser = cache.get("USER") as User | null;

  const loadWallets = async () => {
    try {
      setLoading(true);
      const data = await fetchWallets();
      setWallets(data as WalletType[]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWallets();

    if (cachedUser && !cachedUser.phoneNumber) {
      setPhoneRequired(true);
      setShowAddPhoneNumber(true);
    }
  }, []);

  const requirePhoneNumber = (): boolean => {
    if (phoneRequired) {
      setShowAddPhoneNumber(true);
      return false;
    }
    return true;
  };

  // ----- Wallet actions
  const handleWalletClick = (wallet: WalletType) => {
    setSelectedWallet(wallet);
    actions.openModal();
  };

  const handleFundWallet = () => {
    actions.closeModal();
    fund.openModal();
  };

  const handleWithdraw = () => {
    actions.closeModal();
    withdraw.openModal();
  };

  const handleTransfer = () => {
    actions.closeModal();
    transfer.openModal();
  };

  const handleViewTransactions = () => {
    actions.closeModal();
    if (selectedWallet) {
      navigate(`/wallet/transactions/${selectedWallet.currency}`);
    }
  };

  const handlePhoneAdded = () => {
    setShowAddPhoneNumber(false);
    setPhoneRequired(false);
  };

  const handleWithdrawSuccess = () => {
    <Toast
      variant="success"
      title="Withdrawal Successful"
      message="Your account has been debited"
    />;
  };

  // add — single handler used by every modal's onSuccess
  const handleTransactionSuccess = (transaction?: Transaction) => {
    console.log("handleTransactionSuccess called with:", transaction)
    loadWallets();
    if (transaction) setSummaryTransaction(transaction);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title="Wallet"
        description="Create a wallet to start receiving and sending money."
      />
      <PageBreadcrumb pageTitle="Wallet" />

      <div className="p-6">
        {wallets.length === 0 ? (
          <EmptyWalletState
            onCreate={() => {
              if (!requirePhoneNumber()) return;
              create.openModal();
            }}
          />
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">
                  My Wallets
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Manage your currency wallets
                </p>
              </div>

              <div className="flex items-center gap-1">
                <button onClick={loadWallets} className="refresh-button">
                  <RefreshIcon className="w-7 h-7" />
                </button>
                <button
                  onClick={() => {
                    if (!requirePhoneNumber()) return;
                    create.openModal();
                  }}
                  className="rounded-lg bg-primary px-4 py-2 border border-primary text-gray-800 dark:text-white/90"
                >
                  Create Wallet
                </button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {wallets
                .filter((wallet) => !["ETB", "CDF"].includes(wallet.currency))
                .map((wallet) => (
                  <WalletCard
                    key={wallet.id}
                    wallet={wallet}
                    onClick={() => handleWalletClick(wallet)}
                  />
                ))}
            </div>
          </>
        )}

        {/* ---- Create Wallet */}
        <CreateWalletModal
          isOpen={create.isOpen}
          onClose={create.closeModal}
          onCreated={loadWallets}
          existingWallets={wallets}
        />

        {/* ---- FORCE PHONE NUMBER */}
        <AddPhoneNumber
          isOpen={showAddPhoneNumber}
          onClose={() => setShowAddPhoneNumber(false)}
          onSuccess={handlePhoneAdded}
        />

        {/* ---- Wallet Actions */}
        <WalletActionsModal
          isOpen={actions.isOpen}
          onClose={actions.closeModal}
          wallet={selectedWallet}
          onFundWallet={handleFundWallet}
          onWithdraw={handleWithdraw}
          onViewTransactions={handleViewTransactions}
          onTransfer={handleTransfer}
        />

        <FundWalletModal
          isOpen={fund.isOpen}
          onClose={fund.closeModal}
          wallet={selectedWallet}
          onSuccess={handleTransactionSuccess} 
        />

        <TransferModal
          isOpen={transfer.isOpen}
          onClose={transfer.closeModal}
          wallet={selectedWallet}
          onSuccess={handleTransactionSuccess} 
        />

        <WithdrawModal
          isOpen={withdraw.isOpen}
          onClose={withdraw.closeModal}
          wallet={selectedWallet}
          onSuccess={(transaction?: Transaction) => { {/* was: onSuccess={() => {...}} */}
            handleTransactionSuccess(transaction);
            handleWithdrawSuccess();
          }}
        />

        <TransactionsModal
          isOpen={transactions.isOpen}
          onClose={transactions.closeModal}
          wallet={selectedWallet}
        />

        {/* ---- Transaction Summary — add */}
        <TransactionSummary
          isOpen={!!summaryTransaction}
          onClose={() => setSummaryTransaction(null)}
          transaction={summaryTransaction}
          wallet={selectedWallet}
        />
      </div>
    </>
  );
};

export default Wallet;